import type { ErrorEvent, Event } from "@sentry/react";

import {
	browserProfilingIntegration,
	browserTracingIntegration,
	captureConsoleIntegration,
	contextLinesIntegration,
	extraErrorDataIntegration,
	httpClientIntegration,
	init,
	launchDarklyIntegration,
	replayIntegration,
	thirdPartyErrorFilterIntegration,
} from "@sentry/react";

type ShouldDrop = (event: ErrorEvent, rejection: unknown) => boolean;

// DataCloneError comes from Sentry trying to serialize non-serializable objects
const isDataCloneError: ShouldDrop = (_, rejection) => {
	return (
		rejection instanceof Error &&
		(rejection.name === "DataCloneError" || rejection.message.includes("can not be cloned"))
	);
};

// React Query's skipToken raises "Missing queryFn"; `enabled` is unreliable in prod and creates noise
const isMissingQueryFnError: ShouldDrop = (_, rejection) => {
	return rejection instanceof Error && rejection.message.includes("Missing queryFn");
};

// "Cross-origin stylesheet" errors from Sentry Replay.
// Logged via console.warn so they reach us as console-captured message events.
const isCrossOriginStylesheetError: ShouldDrop = (event) => {
	const texts = [
		event.message ?? "",
		...(event.exception?.values?.map((value) => {
			return value.value ?? "";
		}) ?? []),
	];
	return texts.some((text) => {
		return (
			text.includes("Cannot get CSS styles from text's parentNode") ||
			text.includes("CSSStyleSheet.cssRules") ||
			text.includes("cross-origin stylesheet")
		);
	});
};

const isHttp5xxError: ShouldDrop = (event) => {
	const status = event.contexts?.response?.status_code;
	return typeof status === "number" && status >= 500 && status < 600;
};

// Exception triggered by an unknown browser extension (UX not impacted)
const isWallet4001BrowserExtension: ShouldDrop = (_, rejection) => {
	return (
		typeof rejection === "object" &&
		rejection !== null &&
		"code" in rejection &&
		"message" in rejection &&
		(rejection as { code: unknown }).code === 4001 &&
		(rejection as { message: unknown }).message === "wallet must has at least one account"
	);
};

const dropConditions: ShouldDrop[] = [
	isHttp5xxError,
	isCrossOriginStylesheetError,
	isDataCloneError,
	isMissingQueryFnError,
	isWallet4001BrowserExtension,
];

// Strip response_content_length attribute for statement-letter fetches to prevent Large HTTP Payload errors
function stripStatementLetterPayloadSize<T extends Event>(event: T): T {
	if (!event.transaction?.includes("/statements")) {
		return event;
	}

	for (const span of event.spans ?? []) {
		const url = String(span.data["http.url"] ?? span.description ?? "");
		if (!url.includes("statement-letter")) {
			continue;
		}
		delete span.data["http.response_content_length"];
	}

	return event;
}

export function initSentry(): void {
	init({
		beforeSend(event, hint) {
			return dropConditions.some((check) => {
				return check(event, hint.originalException);
			})
				? null
				: event;
		},
		beforeSendTransaction(event) {
			return stripStatementLetterPayloadSize(event);
		},
		dsn: import.meta.env.VITE_SENTRY_DSN,
		enabled: import.meta.env.VITE_SENTRY_ENABLED === "true",
		environment: import.meta.env.VITE_APP_ENV,
		ignoreErrors: [
			// Backend triggered 401 status code
			"Unauthorized",
			'ObjectMultiplex - orphaned data for stream "metamask-multichain-provider"',
			// Amplitude requests blocked client-side (ad blockers, firewalls), logged via console
			/Amplitude Logger \[(Error|Warn)\]: (Failed to fetch|Event rejected due to exceeded retry count|\{code: 0, error: "Status 'failed')/,
			"LaunchDarkly initialization timed out",
		],
		integrations: [
			extraErrorDataIntegration({
				captureErrorCause: true,
				depth: 5,
			}),
			browserProfilingIntegration(),
			browserTracingIntegration(),
			httpClientIntegration(),
			captureConsoleIntegration({ levels: ["error", "warn"] }),
			contextLinesIntegration(),
			replayIntegration({
				blockAllMedia: false,
				maskAllText: false,
			}),
			launchDarklyIntegration(),
			thirdPartyErrorFilterIntegration({
				behaviour: "drop-error-if-contains-third-party-frames",
				filterKeys: ["fuse-portal-v3"],
			}),
		],
		normalizeDepth: 4, // limit serialization depth
		replaysOnErrorSampleRate: 1.0,
		replaysSessionSampleRate: 0.1,
		tracesSampleRate: 1.0,
	});
}
